import type { LinkBioButtonErrorField } from './link-bio-editor-button-validation';
import type { LinkBioDraftValidationErrors } from './link-bio-editor-validation';

/**
 * Dotted paths that address one field error inside `LinkBioDraftValidationErrors`, used as the keys of
 * the editor's "touched" set: `featuredMediaUrl`, `buttons.<id>.url` (built-ins too, e.g. `buttons.builtin-call.phone`),
 * `socialLinks.<id>`, `theme.primaryColor`. Item ids match `[A-Za-z0-9_-]`, so they never contain a dot.
 */
export const linkBioFieldPath = {
  profile: (field: string) => field,
  button: (buttonId: string, field: LinkBioButtonErrorField) => `buttons.${buttonId}.${field}`,
  socialLink: (linkId: string) => `socialLinks.${linkId}`,
  theme: (field: string) => `theme.${field}`,
  popup: (field: 'imageUrl' | 'url' | 'endDate') => `popup.${field}`,
} as const;

type ErrorTree = { [key: string]: string | ErrorTree | undefined };

/** Every leaf path that currently carries an error message. */
export function listLinkBioErrorPaths(errors: LinkBioDraftValidationErrors): string[] {
  const paths: string[] = [];
  walk(errors as ErrorTree, '', (path) => paths.push(path));
  return paths;
}

/**
 * Same nested shape as `errors`, keeping only the leaves `isVisible` accepts (empty branches dropped),
 * so field components keep reading `errors.buttons?.[id]?.url` unchanged.
 */
export function filterLinkBioErrors(
  errors: LinkBioDraftValidationErrors,
  isVisible: (path: string) => boolean
): LinkBioDraftValidationErrors {
  return (prune(errors as ErrorTree, '', isVisible) ?? {}) as LinkBioDraftValidationErrors;
}

function walk(tree: ErrorTree, prefix: string, visit: (path: string) => void) {
  for (const [key, value] of Object.entries(tree)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') visit(path);
    else if (value) walk(value, path, visit);
  }
}

function prune(tree: ErrorTree, prefix: string, isVisible: (path: string) => boolean): ErrorTree | null {
  const kept: ErrorTree = {};
  for (const [key, value] of Object.entries(tree)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') {
      if (isVisible(path)) kept[key] = value;
    } else if (value) {
      const branch = prune(value, path, isVisible);
      if (branch) kept[key] = branch;
    }
  }
  return Object.keys(kept).length > 0 ? kept : null;
}
